/**
 * Products and verticals (ADR-69).
 *
 * One auction core, one tenant model, one lot-kind registry; what differs
 * between a HipNumber sale and a LotNumber sale is DATA declared here: the
 * default auction mode a new sale takes, the default vertical, the lot term
 * ("Hip 12" versus "Lot 12") and which brand shell a storefront host serves.
 * A vertical declares which kinds exist in it and which of those a sale
 * takes when nothing is configured.
 *
 * Three dimensions stay separate (the brief's STOP rule): PRODUCT is where a
 * sale lives, AUCTION MODE is how it bids, LOT KIND is what is being sold.
 * Nothing in this file reads two of them in one condition, and the
 * boundaries test greps the whole repo for that shape.
 *
 * Pure: no database, no I/O. Stored values arrive as plain strings (the db
 * package does not depend on this registry) and every resolver here fails
 * closed against the vocabulary: an unknown product or vertical yields no
 * kinds and no defaults, never a guess.
 */

import { KIND_KEYS, normalizeKind } from '@hipnumber/lot-kinds';

export const PRODUCT_KEYS = ['HIPNUMBER', 'LOTNUMBER'] as const;
export type ProductKey = (typeof PRODUCT_KEYS)[number];

export const VERTICAL_KEYS = ['LIVESTOCK', 'GENERAL'] as const;
export type VerticalKey = (typeof VERTICAL_KEYS)[number];

/** The product every existing sale, tenant and host belongs to until told
 *  otherwise (migration 0091 backfills it). */
export const DEFAULT_PRODUCT: ProductKey = 'HIPNUMBER';

export type AuctionModeKey = 'LIVE' | 'TIMED' | 'LIVE_WITH_PREBIDDING';

export interface VerticalDefinition {
  readonly key: VerticalKey;
  readonly label: string;
  /** Every registry kind that may appear in a sale of this vertical. */
  readonly kinds: readonly string[];
  /** The kinds a sale takes when neither the company nor the sale configured
   *  an allow-list. LIVESTOCK keeps the pre-ADR-69 behaviour (equine only)
   *  so no existing sale changes. */
  readonly defaultKinds: readonly string[];
}

export interface ProductDefinition {
  readonly key: ProductKey;
  readonly label: string;
  /** What a new sale's auction_mode is when neither the request nor the
   *  company default says. A DEFAULT, never a restriction: a LotNumber sale
   *  may run LIVE and a HipNumber sale may run TIMED. */
  readonly defaultMode: AuctionModeKey;
  readonly defaultVertical: VerticalKey;
  /** Verticals a sale of this product may declare. */
  readonly verticals: readonly VerticalKey[];
  /** Kinds this product can ever show; intersected with the vertical. */
  readonly kinds: readonly string[];
  /** The catalog number's noun ("Hip 12" / "Lot 12"). Data only in Phase 1;
   *  the storefront shell reads it in Phase 3. */
  readonly lotTerm: 'Hip' | 'Lot';
  /** The brand shell key a storefront host carries (tenant_domains.product). */
  readonly shell: 'hipnumber' | 'lotnumber';
  /** How a TIMED sale of this product closes when neither the company nor
   *  the sale said (ADR-69 Phase 2, open question 6). DEFAULTS, never
   *  restrictions: every field is overridable per company and per sale. */
  readonly timedDefaults: TimedSchedulePolicy;
}

/** The closing patterns and soft-close presets auction-core ships (ADR-42).
 *  Mirrored as literals because this package cannot import auction-core
 *  (the db package, which stores them, cannot either); the control-api
 *  timed-schedule test pins all three copies to the engine's. */
export const TIMED_CLOSE_PATTERNS = ['INDIVIDUAL', 'STAGGERED', 'GROUPED'] as const;
export type TimedClosePattern = (typeof TIMED_CLOSE_PATTERNS)[number];
export const TIMED_CLOSE_PRESET_KEYS = ['standard-2m', 'quick-30s', 'hard-close'] as const;
export type TimedClosePresetKey = (typeof TIMED_CLOSE_PRESET_KEYS)[number];
/** ADR-70 cut 6h: a preset, or the office's own soft close. A product's
 *  defaults always name a preset. */
export type TimedCloseChoice = TimedClosePresetKey | 'custom';

/** ADR-70 cut 6h (hitlist 18): a soft close with its own numbers, in seconds
 *  (CustomCloseSchema in @hipnumber/db holds the bounds). */
export interface CustomCloseRule {
  readonly windowSeconds: number;
  readonly extendSeconds: number;
  readonly extendFrom: 'BID_TIME' | 'SCHEDULED_CLOSE';
  readonly maxExtensions: number | null;
}

/** The policy half of a timed schedule: everything but the first close. */
export interface TimedSchedulePolicy {
  readonly closePattern: TimedClosePattern;
  /** STAGGERED: seconds between lots; GROUPED: seconds between groups. */
  readonly staggerSeconds: number;
  /** GROUPED: lots per close group. */
  readonly lotsPerGroup: number;
  readonly closePreset: TimedClosePresetKey;
}

/** Both products close the same way until a company says otherwise: lots
 *  one minute apart, each extending two minutes on a late bid. The ADR's
 *  open question 6 asked whether LotNumber wants quick-30s; the answer is a
 *  one-line change here, not a migration. */
const STANDARD_TIMED_DEFAULTS: TimedSchedulePolicy = {
  closePattern: 'STAGGERED',
  staggerSeconds: 60,
  lotsPerGroup: 1,
  closePreset: 'standard-2m',
};

const ALL_KINDS: readonly string[] = KIND_KEYS;
const NON_LIVING_KINDS: readonly string[] = ['tack', 'trailer', 'notice', 'general'];

export const VERTICALS: Readonly<Record<VerticalKey, VerticalDefinition>> = {
  LIVESTOCK: { key: 'LIVESTOCK', label: 'Livestock', kinds: ALL_KINDS, defaultKinds: ['equine'] },
  GENERAL: { key: 'GENERAL', label: 'General', kinds: NON_LIVING_KINDS, defaultKinds: NON_LIVING_KINDS },
};

export const PRODUCTS: Readonly<Record<ProductKey, ProductDefinition>> = {
  HIPNUMBER: {
    key: 'HIPNUMBER', label: 'HipNumber', defaultMode: 'LIVE', defaultVertical: 'LIVESTOCK',
    verticals: ['LIVESTOCK', 'GENERAL'], kinds: ALL_KINDS, lotTerm: 'Hip', shell: 'hipnumber',
    timedDefaults: STANDARD_TIMED_DEFAULTS,
  },
  LOTNUMBER: {
    key: 'LOTNUMBER', label: 'LotNumber', defaultMode: 'TIMED', defaultVertical: 'GENERAL',
    verticals: ['GENERAL', 'LIVESTOCK'], kinds: ALL_KINDS, lotTerm: 'Lot', shell: 'lotnumber',
    timedDefaults: STANDARD_TIMED_DEFAULTS,
  },
};

export function isProductKey(v: unknown): v is ProductKey {
  return typeof v === 'string' && (PRODUCT_KEYS as readonly string[]).includes(v);
}

export function isVerticalKey(v: unknown): v is VerticalKey {
  return typeof v === 'string' && (VERTICAL_KEYS as readonly string[]).includes(v);
}

/** The product for a stored key, or null when the key is not one we ship. */
export function getProduct(key: unknown): ProductDefinition | null {
  return isProductKey(key) ? PRODUCTS[key] : null;
}

export function getVertical(key: unknown): VerticalDefinition | null {
  return isVerticalKey(key) ? VERTICALS[key] : null;
}

// ---------------------------------------------------------------------------
// Tenant policy: which products a company may create sales under.
// ---------------------------------------------------------------------------

/** What tenants.settings.products looks like as stored: plain strings, so
 *  the db package needs no registry. Resolved here, fail-closed. */
export interface StoredProductsSetting {
  readonly allowed?: readonly string[] | undefined;
  readonly default?: string | undefined;
}

export interface TenantProductPolicy {
  /** Products this company may create sales under. Never empty. */
  readonly allowed: readonly ProductKey[];
  /** The product a new sale takes when the request names none. Always one
   *  of `allowed`. */
  readonly default: ProductKey;
}

/** Nothing configured = HipNumber only, exactly as before ADR-69. Unknown
 *  keys are dropped, a default outside the allowed list falls back to the
 *  first allowed product. A tenant with both products allowed is the Griffin
 *  case; the tenant itself never records "is a LotNumber tenant". */
export function tenantProductPolicy(raw: StoredProductsSetting | null | undefined): TenantProductPolicy {
  const allowed: ProductKey[] = [];
  for (const k of raw?.allowed ?? []) if (isProductKey(k) && !allowed.includes(k)) allowed.push(k);
  if (allowed.length === 0) allowed.push(DEFAULT_PRODUCT);
  const requested = raw?.default;
  const fallback = allowed[0]!;
  return { allowed, default: isProductKey(requested) && allowed.includes(requested) ? requested : fallback };
}

export type ResolvedProduct =
  | { readonly ok: true; readonly product: ProductKey }
  | { readonly ok: false; readonly reason: 'unknown_product' | 'product_not_allowed'; readonly allowed: readonly ProductKey[] };

/** The product a new sale runs under: the request's choice when it names
 *  one the company may use, else the company default. */
export function resolveSaleProduct(requested: string | null | undefined, policy: TenantProductPolicy): ResolvedProduct {
  if (requested === undefined || requested === null) return { ok: true, product: policy.default };
  if (!isProductKey(requested)) return { ok: false, reason: 'unknown_product', allowed: policy.allowed };
  if (!policy.allowed.includes(requested)) return { ok: false, reason: 'product_not_allowed', allowed: policy.allowed };
  return { ok: true, product: requested };
}

export type ResolvedVertical =
  | { readonly ok: true; readonly vertical: VerticalKey }
  | { readonly ok: false; readonly reason: 'unknown_vertical' | 'vertical_not_in_product'; readonly allowed: readonly VerticalKey[] };

/** The vertical a sale serves: the request's choice when the product offers
 *  it, else the product's default vertical. */
export function resolveSaleVertical(requested: string | null | undefined, product: ProductKey): ResolvedVertical {
  const def = PRODUCTS[product];
  if (requested === undefined || requested === null) return { ok: true, vertical: def.defaultVertical };
  if (!isVerticalKey(requested)) return { ok: false, reason: 'unknown_vertical', allowed: def.verticals };
  if (!def.verticals.includes(requested)) return { ok: false, reason: 'vertical_not_in_product', allowed: def.verticals };
  return { ok: true, vertical: requested };
}

/** The auction mode a new sale is born with when the request names none:
 *  the company's saleDefaults.auctionMode if it set one (an explicit tenant
 *  choice outranks a platform default), else the product's default. Unknown
 *  product = LIVE, the column default, so a bad key never changes the engine. */
export function defaultAuctionModeFor(product: string, tenantDefault: AuctionModeKey | null | undefined): AuctionModeKey {
  return tenantDefault ?? getProduct(product)?.defaultMode ?? 'LIVE';
}

export type SaleCreationDefaults =
  | { readonly ok: true; readonly product: ProductKey; readonly vertical: VerticalKey; readonly auctionMode: AuctionModeKey }
  | Extract<ResolvedProduct, { ok: false }>
  | Extract<ResolvedVertical, { ok: false }>;

/** Everything a sale needs at birth from the product dimension, resolved in
 *  one place: product (request > company default), vertical (request >
 *  product default) and auction mode (request > company default > product
 *  default). The create route calls this and nothing else. */
export function saleCreationDefaults(
  requested: { readonly product?: string | undefined; readonly vertical?: string | undefined; readonly auctionMode?: AuctionModeKey | undefined },
  tenant: { readonly products?: StoredProductsSetting | null | undefined; readonly auctionMode?: AuctionModeKey | null | undefined },
): SaleCreationDefaults {
  const product = resolveSaleProduct(requested.product, tenantProductPolicy(tenant.products));
  if (!product.ok) return product;
  const vertical = resolveSaleVertical(requested.vertical, product.product);
  if (!vertical.ok) return vertical;
  return {
    ok: true,
    product: product.product,
    vertical: vertical.vertical,
    auctionMode: requested.auctionMode ?? defaultAuctionModeFor(product.product, tenant.auctionMode),
  };
}

// ---------------------------------------------------------------------------
// Kind availability: a filter over the global registry, never a registry.
// ---------------------------------------------------------------------------

/**
 * The lot kinds available to ONE sale: registry, intersected with the
 * product's kinds, the vertical's kinds, and the configured allow-list
 * (sale override, else company list, resolved by the caller through
 * `configuredItemTypes` in @hipnumber/db) or, when nothing is configured,
 * the vertical's defaults. Configured order is kept so the office picker
 * shows kinds in the order the company listed them; a configured kind the
 * vertical does not carry is dropped, never an error, so a company list
 * written for livestock sales does not break a general sale.
 *
 * Every lot write boundary (office add and edit, import, the consign form,
 * Annie materialization) and the admin kind picker call this and nothing
 * else. Unknown product or vertical = no kinds (fail closed).
 */
export function effectiveKinds(input: {
  readonly product: string;
  readonly vertical: string;
  readonly configured?: readonly string[] | null | undefined;
}): readonly string[] {
  const product = getProduct(input.product);
  const vertical = getVertical(input.vertical);
  if (!product || !vertical) return [];
  const universe = KIND_KEYS.filter((k) => product.kinds.includes(k) && vertical.kinds.includes(k));
  const wanted = input.configured && input.configured.length > 0 ? input.configured.map(normalizeKind) : vertical.defaultKinds;
  const out: string[] = [];
  for (const k of wanted) if (universe.includes(k) && !out.includes(k)) out.push(k);
  return out;
}

/** Is this kind available to the sale? The one check the write boundaries make. */
export function isKindAvailable(kind: string, available: readonly string[]): boolean {
  return available.includes(normalizeKind(kind));
}

// ---------------------------------------------------------------------------
// Timed schedule (ADR-69 Phase 2): the ONE resolver for how a TIMED sale's
// lots close. Sale override, then the company's saleDefaults.timedSchedule,
// then the product's defaults, FIELD BY FIELD: a schedule is not a money
// term, so the 0033 wholesale rule does not apply (a sale that sets only its
// first close must still get the company's pattern). The open time is the
// sale's starts_at and is not resolved here.
// ---------------------------------------------------------------------------

/** What the db layer hands over, already parsed (SaleTimedScheduleSchema /
 *  TimedScheduleDefaultsSchema in @hipnumber/db); plain literals so this
 *  package needs no zod and no db. */
export interface StoredTimedSchedule {
  readonly closePattern?: string | undefined;
  readonly staggerSeconds?: number | undefined;
  readonly lotsPerGroup?: number | undefined;
  readonly closePreset?: string | undefined;
  /** With closePreset 'custom' only. */
  readonly customClose?: CustomCloseRule | undefined;
  /** Sale only: ISO instant of the first close. */
  readonly firstCloseAt?: string | undefined;
}

export type TimedScheduleSource = 'SALE' | 'TENANT' | 'PRODUCT';

export interface EffectiveTimedSchedule extends Omit<TimedSchedulePolicy, 'closePreset'> {
  readonly closePreset: TimedCloseChoice;
  /** The custom rule's numbers when closePreset is 'custom', else null.
   *  Always from the same layer as closePreset (its source). */
  readonly customClose: CustomCloseRule | null;
  /** ISO instant of the first close, or null when no layer set one: the
   *  control plane cannot open the lots without it (go-live refuses). */
  readonly firstCloseAt: string | null;
  /** Where each policy field came from, for the office's "company default"
   *  note. firstCloseAt is always SALE when present. */
  readonly sources: Readonly<Record<keyof TimedSchedulePolicy, TimedScheduleSource>>;
}

function isPattern(v: unknown): v is TimedClosePattern {
  return typeof v === 'string' && (TIMED_CLOSE_PATTERNS as readonly string[]).includes(v);
}

function isPreset(v: unknown): v is TimedClosePresetKey {
  return typeof v === 'string' && (TIMED_CLOSE_PRESET_KEYS as readonly string[]).includes(v);
}

const isWhole = (v: unknown, min: number, max: number): boolean => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;

/** The bounds of CustomCloseSchema, checked again here: the db parse is the
 *  gate, this keeps a hand-built input from reaching the ring malformed. */
export function isCustomCloseRule(v: unknown): v is CustomCloseRule {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  return isWhole(r['windowSeconds'], 5, 3600)
    && isWhole(r['extendSeconds'], 5, 3600)
    && (r['extendFrom'] === 'BID_TIME' || r['extendFrom'] === 'SCHEDULED_CLOSE')
    && (r['maxExtensions'] === null || isWhole(r['maxExtensions'], 1, 1000));
}

/** One layer's close rule, or undefined when the layer sets none it can
 *  stand behind: 'custom' without usable numbers is "not set here", like an
 *  unknown preset, so the next layer decides. */
function closeRuleOf(layer: StoredTimedSchedule | null | undefined): { readonly preset: TimedCloseChoice; readonly custom: CustomCloseRule | null } | undefined {
  if (isPreset(layer?.closePreset)) return { preset: layer.closePreset, custom: null };
  if (layer?.closePreset === 'custom' && isCustomCloseRule(layer.customClose)) {
    const c = layer.customClose;
    return { preset: 'custom', custom: { windowSeconds: c.windowSeconds, extendSeconds: c.extendSeconds, extendFrom: c.extendFrom, maxExtensions: c.maxExtensions } };
  }
  return undefined;
}

/** Unknown product = the standard defaults, so a bad stored key still yields
 *  a schedule the office can see and fix rather than a crash. */
export function productTimedDefaults(product: string): TimedSchedulePolicy {
  return getProduct(product)?.timedDefaults ?? STANDARD_TIMED_DEFAULTS;
}

export function effectiveTimedSchedule(input: {
  readonly product: string;
  readonly tenantDefault: StoredTimedSchedule | null | undefined;
  readonly sale: StoredTimedSchedule | null | undefined;
}): EffectiveTimedSchedule {
  const base = productTimedDefaults(input.product);
  const pick = <T>(
    saleValue: T | undefined,
    tenantValue: T | undefined,
    productValue: T,
  ): { readonly value: T; readonly source: TimedScheduleSource } => {
    if (saleValue !== undefined) return { value: saleValue, source: 'SALE' };
    if (tenantValue !== undefined) return { value: tenantValue, source: 'TENANT' };
    return { value: productValue, source: 'PRODUCT' };
  };
  const pattern = pick(
    isPattern(input.sale?.closePattern) ? input.sale.closePattern : undefined,
    isPattern(input.tenantDefault?.closePattern) ? input.tenantDefault.closePattern : undefined,
    base.closePattern,
  );
  const stagger = pick(input.sale?.staggerSeconds, input.tenantDefault?.staggerSeconds, base.staggerSeconds);
  const group = pick(input.sale?.lotsPerGroup, input.tenantDefault?.lotsPerGroup, base.lotsPerGroup);
  // The preset and a custom rule's numbers are one field: whichever layer
  // names the rule supplies its numbers too, never a mix of two layers.
  const preset = pick(closeRuleOf(input.sale), closeRuleOf(input.tenantDefault), { preset: base.closePreset as TimedCloseChoice, custom: null });
  return {
    firstCloseAt: input.sale?.firstCloseAt ?? null,
    closePattern: pattern.value,
    staggerSeconds: stagger.value,
    lotsPerGroup: group.value,
    closePreset: preset.value.preset,
    customClose: preset.value.custom,
    sources: {
      closePattern: pattern.source,
      staggerSeconds: stagger.source,
      lotsPerGroup: group.source,
      closePreset: preset.source,
    },
  };
}

// ---------------------------------------------------------------------------
// Brand shells (ADR-69 Phase 3): what a storefront HOST wears. Keyed by
// tenant_domains.product through the product's `shell`; read by the layout,
// the footer, the unavailable page and the email sender's display name.
// Presentation only: nothing about bidding, kinds or mode lives here, and a
// host's shell never decides a sale's product (the sale row does).
// ---------------------------------------------------------------------------

export const SHELL_KEYS = ['hipnumber', 'lotnumber'] as const;
export type ShellKey = (typeof SHELL_KEYS)[number];

export interface ShellDefinition {
  readonly key: ShellKey;
  /** The brand name as written: page titles, "Powered by", email display names. */
  readonly name: string;
  /** The word in the top-bar mark, beside the "24" tag both brands share. */
  readonly markWord: string;
  /** The brand's own site for the footer link. Null = no apex chosen yet
   *  (open question 1), so the footer line renders as text, never a dead link. */
  readonly siteUrl: string | null;
  /** Whether the footer line carries the trademark sign (registered mark only). */
  readonly trademark: boolean;
  /** The From display name on the ONE verified sending address; the address
   *  itself never changes per shell (the sending domain is verified once). */
  readonly emailFromName: string;
  /** The registrable domain the brand's own hosts live under (ADR-69 section
   *  13): admin., platform., console. and api.<siteDomain> are that brand's
   *  doors into the ONE platform. It picks the shell a staff host wears and is
   *  the cookie site a staff session lives on; it never grants authority. */
  readonly siteDomain: string;
}

export const SHELLS: Readonly<Record<ShellKey, ShellDefinition>> = {
  hipnumber: { key: 'hipnumber', name: 'HipNumber', markWord: 'HIPNUMBER', siteUrl: 'https://hipnum.com', trademark: true, emailFromName: 'HipNumber', siteDomain: 'hipnum.com' },
  lotnumber: { key: 'lotnumber', name: 'LotNumber', markWord: 'LOTNUMBER', siteUrl: null, trademark: false, emailFromName: 'LotNumber', siteDomain: 'lotnumber.com' },
};

/** The shell every host wore before ADR-69, and the one an unknown key renders. */
export const DEFAULT_SHELL: ShellKey = 'hipnumber';

export function isShellKey(v: unknown): v is ShellKey {
  return typeof v === 'string' && (SHELL_KEYS as readonly string[]).includes(v);
}

/** The shell a stored product key maps to. Unknown = the default shell: a
 *  host must still render, and every host before ADR-69 is HipNumber. */
export function shellForProduct(product: unknown): ShellKey {
  return getProduct(product)?.shell ?? DEFAULT_SHELL;
}

/** The shell definition for a shell key, or for a product key when the
 *  caller holds the sale's product rather than the host's shell. */
export function getShell(key: unknown): ShellDefinition {
  if (isShellKey(key)) return SHELLS[key];
  return SHELLS[shellForProduct(key)];
}

/** The brand whose site a hostname sits under (its apex or any subdomain), or
 *  null for a host that is neither brand's (a laptop, an execute-api host).
 *  Matching is on whole labels: "nothipnum.com" is not under hipnum.com. */
export function brandSiteOf(hostname: string): ShellKey | null {
  const h = hostname.trim().toLowerCase().replace(/\.$/, '');
  for (const key of SHELL_KEYS) {
    const domain = SHELLS[key].siteDomain;
    if (h === domain || h.endsWith(`.${domain}`)) return key;
  }
  return null;
}

/** The shell a staff host wears (admin., platform., console., api.): its
 *  brand site's, HipNumber for anything else. Presentation only: the host
 *  never decides authority, and a sale's product still comes from the sale. */
export function shellForHost(hostname: string): ShellKey {
  return brandSiteOf(hostname) ?? DEFAULT_SHELL;
}

/** The same door under another brand's site: https://admin.hipnum.com becomes
 *  https://admin.lotnumber.com for the LotNumber shell, path and query kept. A
 *  URL that is on no brand site (a laptop's http://localhost:4400) or is
 *  already on the asked brand comes back unchanged. */
export function brandUrl(url: string, shell: ShellKey): string {
  const m = /^([a-z][a-z0-9+.-]*:\/\/)([^/:?#]+)(.*)$/i.exec(url);
  if (!m) return url;
  const [, scheme, host, rest] = m as unknown as [string, string, string, string];
  const from = brandSiteOf(host);
  if (from === null || from === shell) return url;
  const sub = host.slice(0, host.length - SHELLS[from].siteDomain.length);
  return `${scheme}${sub}${SHELLS[shell].siteDomain}${rest}`;
}

/** The product a new-sale form preselects on a staff host (ADR-69 section 13):
 *  on another brand's site (admin.lotnumber.com) that brand's product when the
 *  company may use it; everywhere else, and when the company may not, the
 *  company default exactly as before. A preselection only: the form sends the
 *  choice, resolveSaleProduct still decides, and the stored sale row, never
 *  the host, drives the sale from then on. */
export function newSaleProductDefault(policy: TenantProductPolicy, hostname: string): ProductKey {
  const site = brandSiteOf(hostname);
  if (site === null || site === DEFAULT_SHELL) return policy.default;
  return policy.allowed.find((k) => PRODUCTS[k].shell === site) ?? policy.default;
}

/** The catalog number's noun for a sale of this product: "Hip" / "Lot".
 *  Unknown = "Hip", the pre-ADR-69 word, so no existing page changes. */
export function lotTermFor(product: unknown): 'Hip' | 'Lot' {
  return getProduct(product)?.lotTerm ?? 'Hip';
}

/** The lower-case route segment the lot term makes: "hip-12" / "lot-12". */
export function lotSlugFor(product: unknown): 'hip' | 'lot' {
  return lotTermFor(product) === 'Lot' ? 'lot' : 'hip';
}
